/**
 * Citation Detection Patterns and Logic
 *
 * IMPORTANT: This file must be kept in sync with the backend implementation in:
 * fp-backend/main/v1/QueryResult/services/inject_citations.py
 *
 * Any changes to the citation detection logic in either file should be
 * reflected in the other to ensure consistent behavior between frontend
 * and backend citation processing.
 *
 * The patterns here determine when citations following numbers should be
 * hidden (made invisible) vs shown as clickable citation buttons.
 */

// ============================================================================
// CHARACTER SETS AND BASIC PATTERNS
// ============================================================================

export const DASH_CHARS = "\\-–—−";
export const CURRENCY_SYMBOLS =
  "$¢£¤¥֏؋߾߿৲৳৻૱௹฿៛₠₡₢₣₤₥₦₧₨₩₪₫€₭₮₯₰₱₲₳₴₵₶₷₸₹₺₻₼₽₾₿";

// ============================================================================
// EXCLUSION PATTERNS - Numbers in these contexts are NOT citable
// ============================================================================

/**
 * Patterns that should never be treated as citable numerics
 * Corresponds to Python: _EXCLUDE_RE
 */
export const EXCLUDE_RE = new RegExp(
  "(?:" +
    "\\[\\d+\\]|" + // Numeric list marks like "[12]"
    "10-?[KkQq]|" + // SEC form references such as "10-K" or "10-Q"
    "[qQ][1234]|" + // Quarter references like "Q1", "q3"
    "^\\s*\\d+[.)]\\s+(?=[\\w*`~_])|" + // Ordered-list prefixes such as "1." at the start of a line
    "(?:Start Date|End Date):.*<br>|" + // Lines starting with "Start Date:" or "End Date:" followed by HTML <br>
    // Page/pg references like "(p.17)", "page 3", "pg 8", "p17". The left
    // boundary is load-bearing: without it any word ENDING in "p" followed
    // by a number matched — "up 40%" lost its "p 40" and the figure was
    // destroyed before scanning (backend twin fixed in #11454; this mirror
    // had drifted).
    "\\(?\\s*(?<![A-Za-z])(?:p(?:age)?|pg)\\.??\\s*\\d+\\s*\\)?|" +
    "(?:Fund|Series|Phase|Grade|Class|Tier|Group|Part|Book|Chapter|Volume|Version|Lot|Tract|Pool|Tranche|Vehicle)\\s+\\d{1,4}(?=[,.\\s);\\]]|$)|" + // Entity name numbers like "Fund 2", "Series 1"
    "(?:Filing|Form|Schedule|Item|Exhibit|Appendix|Section)\\s+\\d{1,4}[A-Za-z]?(?=[,.\\s);\\]]|$)" + // Form references like "ADV Filing 2B", "Form 2A" (#10527)
    ")",
  "gmi"
);

/**
 * Month names for date recognition
 * Corresponds to Python: _MONTH_RE
 */
export const MONTH_RE =
  "(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|" +
  "May|Jun(?:e)?|Jul(?:y)?|Aug(?:ust)?|Sep(?:tember)?|" +
  "Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)";

/**
 * Day numbers with optional ordinal suffix
 * Corresponds to Python: _DAY_RE
 */
export const DAY_RE = "(?:3[01]|[12][0-9]|0?[1-9])(?:st|nd|rd|th)?\\b";

/**
 * Complete date pattern - matches various date formats
 * Corresponds to Python: _DATE_RE
 */
export const DATE_RE = new RegExp(
  `(?:` +
    `${MONTH_RE}\\s*${DAY_RE}` + // Month first variant (e.g., "March 3")
    `(?:\\s*[${DASH_CHARS}]\\s*${DAY_RE})?` + // optional day range (e.g., "March 3-5")
    `(?:(?:\\s*,\\s*${DAY_RE})+(?:\\s*,?\\s*(?:and|&)\\s*${DAY_RE})?)?` + // optional comma list
    `(?:\\s*,?\\s*20[0-9]{2})?` + // optional year
    `|` +
    `${DAY_RE}` + // Day first variant (e.g., "3 March")
    `(?:\\s*[${DASH_CHARS}]\\s*${DAY_RE})?` +
    `(?:(?:\\s*,\\s*${DAY_RE})+(?:\\s*,?\\s*(?:and|&)\\s*${DAY_RE})?)?` +
    `\\s*${MONTH_RE}` +
    `(?:\\s*,?\\s*20[0-9]{2})?` +
    `)`,
  "gi"
);

/**
 * Stand-alone 4-digit years (1500-2100)
 * Corresponds to Python: _YEAR_RE
 */
export const YEAR_RE = /\b(?:1[5-9]\d{2}|20\d{2}|2100)(?=s?\b)/g;

/**
 * ISO date format (YYYY-MM-DD or YYYY/MM/DD)
 * Corresponds to Python: _ISO_DATE_RE
 */
export const ISO_DATE_RE =
  /\b(?:15|20)\d{2}[\-\/](?:0?[1-9]|1[0-2])(?:[\-\/](?:0?[1-9]|[12]\d|3[01]))?\b/g;

/**
 * Duration phrases like "12 months", "5 years"
 * Corresponds to Python: _DURATION_RE
 */
export const DURATION_RE = /\b\d+\s*(?:month|months|day|days|year|years)\b/gi;

/**
 * Slash-based fractions like "3/8", "5 1/2"
 * Corresponds to Python: _SLASH_RE
 */
export const SLASH_RE = /\b(?:\d+\s+)?\d+\/\d+(?:\/\d+)?\b/g;

/**
 * Stock index patterns (S&P 500, Russell 2000, etc.)
 * Corresponds to Python: STOCK_INDEX_RE_SRC + STOCK_INDEX_RE
 */
export const STOCK_INDEX_RE = new RegExp(
  "\\bS&P(?:\\s+(?:" +
    "North\\s+American\\s+Technology\\s+Sector\\s+Index|" +
    "LSTA\\s+Leveraged\\s+Loan|" +
    "(?:Small\\s*Cap|Mid\\s*Cap)?\\s*" +
    "(?:100|400|500|600|1000|1200|1250|1500|TSX\\s*60)" +
    "(?:\\s*\\(?\\s*(?:Index|TR|Total\\s*Return)\\s*\\)?)?))?" +
    "|\\bRussell\\s+(?:" +
    "(?:Small\\s*Cap|Mid\\s*Cap)\\s*(?:Index|Benchmark|TR|Total\\s*Return|Net\\s*TR|Net\\s*Return|Gross\\s*Return)?|" +
    "(?:Small\\s*Cap|Mid\\s*Cap)?(?:2K|50|1000|2000|2500|3000|Top\\s*50)" +
    "(?:\\s*(?:Growth|Total\\s*Return|TR))?)" +
    "|\\bNasdaq(?:\\s+Bank\\s+Index|(?:\\s*|-)\\s*100)?" +
    "|\\bDow\\s*Jones\\s*(?:Industrial\\s*Average\\s*)?30" +
    "|\\bTSX\\s*60|\\bTSX\\s*Composite\\s*60" +
    "|\\bBovespa\\s*50|\\bCOLCAP\\s*40|\\bIPSA\\s*40" +
    "|\\bFTSE\\s+(?:(?:Small\\s*Cap|Mid\\s*Cap)?)?(?:100|250|350|MIB\\s*40|All\\s*Share)" +
    "|\\bEURO\\s*STOXX\\s*(?:50|600)" +
    "|\\bSTOXX\\s*[1-9]\\d{1,2}" +
    "|\\bDAX\\s*(?:30|40)" +
    "|\\bCAC\\s*40" +
    "|\\bAEX\\s*25" +
    "|\\bIBEX\\s*35" +
    "|\\bOMX\\w*\\s*\\d{1,3}" +
    "|\\bNikkei\\s*225" +
    "|\\bTOPIX\\s*(?:100|500|[1-9]\\d{0,2})" +
    "|\\bHang\\s*Seng\\s*China\\s*Enterprises\\s*Index\\s*50" +
    "|\\bKOSPI\\s*200" +
    "|\\bKOSDAQ\\s*150" +
    "|\\bSSE\\s*(?:50|180)" +
    "|\\bCSI\\s*(?:100|200|300|500|1000)" +
    "|\\bTAIEX\\s*50" +
    "|\\bASX\\s*200" +
    "|\\bNifty\\s*50" +
    "|\\bBSE\\s*Sensex\\s*30" +
    "|\\bKSE\\s*100" +
    "|\\bKLCI\\s*30" +
    "|\\bNZX\\s*50" +
    "|\\bMSCI\\s+(?:ACWI(?:\\s+IMI)?|World|EAFE|Emerging\\s+Markets|EM|ACWI\\s+ex\\s+USA)" +
    "(?:\\s+(?:Index|Benchmark|TR|Total\\s*Return|Net\\s*TR|Net\\s*Return|Gross\\s*Return))?" +
    "|\\b(?:Bloomberg\\s+)?(?:Barclays\\s+)?(?:US\\s+)?Aggregate\\s+Bond\\s+Index" +
    "|\\bBloomberg\\s+Global\\s+Aggregate\\s+Bond\\s+Index",
  "gi"
);

/**
 * Industry-specific terms containing numbers (Boeing 737, Windows 11, etc.)
 * Corresponds to Python: _INDUSTRY_TERMS_SRC + _DOMAIN_EXCLUDE_RE
 */
export const INDUSTRY_TERMS_RE = new RegExp(
  "\\b\\d+\\s*YR\\s*(?:US\\s+)?Treasury\\s+Futures\\b|" +
    "\\bBoeing\\s+\\d{3}\\b|" +
    "\\bMicrosoft\\s+365\\b|" +
    "\\bOffice\\s+365\\b|" +
    "\\bDynamics\\s+365\\b|" +
    "\\bWindows\\s+(?:7|8(?:\\.1)?|10|11|12)\\b|" +
    "\\biPhone\\s+[1-9]\\d?\\b|" +
    "\\bPixel\\s+[1-9]\\d?\\b|" +
    "\\bTesla\\s+Model\\s+[1-9]\\d?\\b|" +
    "\\bPlayStation\\s+(?:[345])\\b|" +
    "\\bXbox\\s+360\\b|" +
    "\\bMeta\\s+Quest\\s+[1-9]\\d?\\b|" +
    "\\bBMW\\s+(?:3|5|7)\\s+Series\\b|" +
    "\\bFortune\\s+500\\b|" +
    "\\bBig\\s+4\\b|" +
    "\\b500\\s+Startups\\b",
  "gi"
);

// ============================================================================
// CITABLE NUMERIC PATTERNS
// ============================================================================

/**
 * Dollar unit shorthands and multipliers
 * Corresponds to Python: _SUFFIX_UNITS
 */
export const SUFFIX_UNITS =
  "(?:trillion|billion|million|bn|b|mm|m|k|x|t|bp|bps|pp|ppt)";

// The same suffixes for the answer-side token scan, with the "x" multiple
// required adjacent to its digits ("2.5x"). An "x" separated by whitespace
// is the times-operator in a computed formula, mirroring the backend
// injector (#11454).
const ANSWER_SUFFIX_UNITS =
  "(?:\\s*(?:trillion|billion|million|bn|b|mm|m|k|t|bp|bps|pp|ppt)|x)";

/**
 * Main number pattern that matches potentially citable numerics
 * This includes numbers with currency, percentages, magnitude suffixes, etc.
 * Corresponds to Python: _NUM_RE
 */
export const NUM_RE = new RegExp(
  `(?<![A-Za-z0-9%])` + // Not preceded by alphanumeric or %
    `(?<![A-Za-z0-9][${DASH_CHARS}])` + // Not preceded by alnum-dash (COVID-19, uuid segments, range halves — #10527)
    `(?<!\\d\\.)` + // Not mid dotted-sequence ("2023.06.27", #10527)
    `(?<!/)` + // Not after a slash ("+/-0.2", #10527)
    `(?:(?:low|mid|high)\\s*[${DASH_CHARS}]\\s*)?` + // Optional low-/mid-/high- prefix
    // A dash directly after a marker's "]" is a range separator, and a dash
    // followed by whitespace is a subtraction operator — neither is a sign,
    // mirroring the backend injector (#11454).
    `(?:(?<!\\])(?:[<≥≤>≈~]|&lt;|&gt;)|(?<!\\])[${DASH_CHARS}](?!\\s))?\\s*` +
    `(?:[${CURRENCY_SYMBOLS}]\\s*)?` + // Optional currency symbol
    `(?:(?<!\\])-)?\\d+(?:,\\d{3})*(?:\\.\\d+)?` + // Number with optional commas and decimals
    `(?:${ANSWER_SUFFIX_UNITS})?` + // Optional magnitude suffix
    `(?:\\s*%)?` + // Optional percent sign
    `(?:\\+)?` + // Optional trailing +
    // Duration suffixes never stack after a percent: a %-marked figure is
    // complete, and letting "year" attach turned "up 4.7% year-over-year"
    // into a "4.7% year" span (#11454 live testing).
    `(?:(?<!%)[${DASH_CHARS}]?\\s*points?)?` + // Optional -point or points
    `(?:(?<!%)[${DASH_CHARS}]?\\s*years?)?` + // Optional -year or years
    `(?:s)?` + // Allow trailing s (low-30s)
    `(?![A-Za-z0-9])` + // Not followed by alphanumeric
    `(?!\\.\\d)`, // … nor a dotted continuation (#10527)
  "gi"
);

/**
 * A numeric token carries enough "decoration" to be a linkable figure:
 * currency, percent, decimal, thousands separator, a magnitude suffix, or 4+
 * digits. Undecorated 1-3 digit integers are too ambiguous — they highlighted
 * proper nouns ("12 West"), enumeration tokens, and section numerals (#10527).
 * Corresponds to Python: _DECORATED_RE.
 */
export const DECORATED_RE = new RegExp(
  `[.,%]|[${CURRENCY_SYMBOLS}]|(?:${SUFFIX_UNITS})\\b`,
  "i"
);

// ============================================================================
// MAIN DETECTION FUNCTION
// ============================================================================

/**
 * Determines if the text immediately before a citation contains a citable numeric
 * that should cause the citation to be hidden (rendered invisibly).
 *
 * This function implements the same logic as Python's _citable_numerics() function
 * but focused on checking if a citation should be hidden based on what precedes it.
 *
 * @param textBefore - The text that appears before the citation
 * @returns true if the citation should be hidden (follows a citable numeric), false otherwise
 */
export function hasCitableNumericBeforeCitation(textBefore: string): boolean {
  if (!textBefore) return false;

  // Skip numeric citation detection for chart data placeholders
  if (/CHART_DATA_.*_PRESERVED/.test(textBefore)) {
    return false;
  }

  // Check if the text ends with a citable numeric (possibly followed by whitespace)
  // We need to find the last numeric-like pattern and check if it's citable

  // First, remove any trailing whitespace to find the actual last token
  const trimmedText = textBefore.trimEnd();
  if (!trimmedText) return false;

  // Find the last word/token (including numbers with suffixes, currencies, etc.)
  // This pattern matches the last "word" which could be a number with various decorations
  const lastTokenMatch = trimmedText.match(
    new RegExp(
      `(?:[${CURRENCY_SYMBOLS}]\\s*)?` + // Optional currency
        `-?\\d+(?:,\\d{3})*(?:\\.\\d+)?` + // Number
        `(?:\\s*${SUFFIX_UNITS})?` + // Optional units
        `(?:\\s*%)?` + // Optional percent
        `(?:\\+)?` + // Optional plus
        `(?:(?<!%)[${DASH_CHARS}]?\\s*(?:points?|years?))?` + // Optional points/years (never after a %)
        `(?:s)?` + // Optional trailing s
        `$`, // At the end of string
      "i"
    )
  );

  if (!lastTokenMatch) {
    return false;
  }

  const lastToken = lastTokenMatch[0];

  // Now check if this token is in an excluded context
  // We need to look at a bit more context to determine exclusions
  const contextLength = Math.min(100, trimmedText.length);
  const context = trimmedText.slice(-contextLength);

  // Check if this number is part of an excluded pattern

  // Check if it's in a date
  DATE_RE.lastIndex = 0;
  ISO_DATE_RE.lastIndex = 0;
  const isDate = DATE_RE.test(context) || ISO_DATE_RE.test(context);
  DATE_RE.lastIndex = 0;
  ISO_DATE_RE.lastIndex = 0;
  if (isDate) {
    const dateMatches = [
      ...context.matchAll(new RegExp(DATE_RE.source, "gi")),
      ...context.matchAll(new RegExp(ISO_DATE_RE.source, "gi")),
    ];
    for (const match of dateMatches) {
      if (context.endsWith(match[0])) {
        return false; // The number is part of a date at the end
      }
    }
  }

  // Check if it's a standalone year
  YEAR_RE.lastIndex = 0;
  if (YEAR_RE.test(lastToken)) {
    YEAR_RE.lastIndex = 0;
    return false;
  }
  YEAR_RE.lastIndex = 0;

  // Check if it's in a duration phrase
  DURATION_RE.lastIndex = 0;
  const isDuration = DURATION_RE.test(context);
  DURATION_RE.lastIndex = 0;
  if (isDuration) {
    const durationMatches = context.matchAll(
      new RegExp(DURATION_RE.source, "gi")
    );
    for (const match of durationMatches) {
      if (context.endsWith(match[0])) {
        return false;
      }
    }
  }

  // Check if it's part of a fraction
  SLASH_RE.lastIndex = 0;
  if (SLASH_RE.test(lastToken)) {
    SLASH_RE.lastIndex = 0;
    return false;
  }
  SLASH_RE.lastIndex = 0;

  // Check if it's part of a stock index
  STOCK_INDEX_RE.lastIndex = 0;
  const isStockIndex = STOCK_INDEX_RE.test(context);
  STOCK_INDEX_RE.lastIndex = 0;
  if (isStockIndex) {
    const indexMatches = context.matchAll(
      new RegExp(STOCK_INDEX_RE.source, "gi")
    );
    for (const match of indexMatches) {
      if (context.endsWith(match[0])) {
        return false;
      }
    }
  }

  // Check if it's part of an industry term
  INDUSTRY_TERMS_RE.lastIndex = 0;
  const isIndustryTerm = INDUSTRY_TERMS_RE.test(context);
  INDUSTRY_TERMS_RE.lastIndex = 0;
  if (isIndustryTerm) {
    const industryMatches = context.matchAll(
      new RegExp(INDUSTRY_TERMS_RE.source, "gi")
    );
    for (const match of industryMatches) {
      if (context.endsWith(match[0])) {
        return false;
      }
    }
  }

  // Check excluded patterns (list markers, SEC forms, etc.)
  EXCLUDE_RE.lastIndex = 0;
  const hasExcludePattern = EXCLUDE_RE.test(context);
  EXCLUDE_RE.lastIndex = 0;
  if (hasExcludePattern) {
    const excludeMatches = context.matchAll(
      new RegExp(EXCLUDE_RE.source, "gmi")
    );
    for (const match of excludeMatches) {
      if (context.endsWith(match[0])) {
        return false;
      }
    }
  }

  // If we get here, we have a number at the end that's not in an excluded context
  // Now verify it matches the full NUM_RE pattern for citable numerics
  NUM_RE.lastIndex = 0;
  const fullNumMatch = trimmedText.match(
    new RegExp(
      `(?<![A-Za-z0-9%])` + // Not preceded by alphanumeric or %
        `(?<![A-Za-z][${DASH_CHARS}])` + // Not preceded by letter-dash
        `(?:(?:low|mid|high)\\s*[${DASH_CHARS}]\\s*)?` + // Optional prefix
        `(?:[<≥≤>≈~${DASH_CHARS}]|&lt;|&gt;)?\\s*` + // Optional comparison
        `(?:[${CURRENCY_SYMBOLS}]\\s*)?` + // Optional currency
        `-?\\d+(?:,\\d{3})*(?:\\.\\d+)?` + // Number
        `(?:\\s*${SUFFIX_UNITS})?` + // Optional units
        `(?:\\s*%)?` + // Optional percent
        `(?:\\+)?` + // Optional plus
        `(?:(?<!%)[${DASH_CHARS}]?\\s*points?)?` + // Optional points (never after a %)
        `(?:(?<!%)[${DASH_CHARS}]?\\s*years?)?` + // Optional years (never after a %)
        `(?:s)?` + // Optional s
        `$`, // At end
      "i"
    )
  );
  NUM_RE.lastIndex = 0;

  return fullNumMatch !== null;
}

/**
 * Parenthetical legal / section references like "3(c)(7)", "3(c)(1)", or
 * "4(a)(2)" — common in fund docs (Investment Company Act sections). The digits
 * here are part of a citation/section label, not citable figures.
 */
export const SECTION_REFERENCE_RE = /\b\d+\s*(?:\([0-9A-Za-z]+\)\s*)+/g;

/** One number of a dash range, and what it may be looked up as in a source. */
export interface CitableRangeEnd {
  start: number;
  end: number;
  value: string;
  /**
   * The end as written, when that is a figure in its own right, then the end
   * with the unit it shares from the other end ("$540" → "$540B", "69" →
   * "69%"). A bare 1-3 digit end is only ever looked up with its shared unit.
   */
  candidates: string[];
}

export interface CitableNumericMatch {
  start: number;
  end: number;
  value: string;
  /** Set when the figure is a dash range ("$540–900B"); `value` is the whole. */
  range?: { left: CitableRangeEnd; right: CitableRangeEnd };
  /**
   * The second number of a range whose first number the backend already
   * linked. It exists only because the backend declined it, so it binds only
   * when a source's text actually holds it (#12190).
   */
  rangeOrphan?: boolean;
}

// One end of an answer-side dash range. Corresponds to Python: _RANGE_END_SRC.
const RANGE_END = `(?:[${CURRENCY_SYMBOLS}]\\s?)?\\d+(?:,\\d{3})*(?:\\.\\d+)?(?:${ANSWER_SUFFIX_UNITS})?(?:\\s?%)?`;

// Two numbers joined by a dash. An en or em dash may carry one space on each
// side; a hyphen or minus sign must touch both numbers, because with spaces
// around it, it is the subtraction operator of a computed formula (#11454).
// Corresponds to Python: _ANSWER_RANGE_RE.
const RANGE_RE_SRC =
  `(?<![A-Za-z0-9_%.,${CURRENCY_SYMBOLS}])` +
  `(?<![A-Za-z0-9_][${DASH_CHARS}])` +
  `(?<!/)` +
  `(${RANGE_END})(?:\\s?[–—]\\s?|[\\-−])(${RANGE_END})` +
  `(?![A-Za-z0-9_%])(?!\\.\\d)(?![${DASH_CHARS}]\\d)`;

// The unlinked second half of a range, at the very start of the text node that
// follows a backend citation link: the dash, then the number. The separator
// rule is the range's own: only an en or em dash may carry a space.
const RANGE_ORPHAN_RE = new RegExp(
  `^(?:[–—]\\s?|[\\-−])(${RANGE_END})(?![A-Za-z0-9_%])(?!\\.\\d)`,
  "i"
);

// Where a range is never a figure: a bare URL. Corresponds to Python:
// _RANGE_PROTECTED_RE (a markdown link never reaches this scan as text; its
// label and target live in a link node).
const RANGE_PROTECTED_RE_SRC = "https?://[^\\s)\\]]+";

const RANGE_NAME_GUARD_EXEMPT_RE = new RegExp(
  `[${CURRENCY_SYMBOLS}%,]|\\d\\s*${SUFFIX_UNITS}\\b`,
  "i"
);
const RANGE_LEAD_RE = new RegExp(`^[${CURRENCY_SYMBOLS}]`);
const RANGE_TRAIL_RE = new RegExp(`(?:${ANSWER_SUFFIX_UNITS}|\\s?%)+$`, "i");

const isFigureAlone = (written: string): boolean =>
  DECORATED_RE.test(written) || written.replace(/\D/g, "").length >= 4;

const bareDigits = (token: string): string =>
  token.replace(/[^\d.]/g, "").replace(/^\.+|\.+$/g, "");

/**
 * Finds every citable numeric figure in `text` with its position (e.g. the
 * "~24%" and ">13%" in a sentence). Mirrors the legacy backend's
 * `_citable_numerics`: it builds a set of excluded digit-strings by matching
 * the date / year / fraction / duration / stock-index / page-ref patterns over
 * the WHOLE text, then drops any numeric whose digits are in that set. This is
 * value-based (not position-based) so a multi-part date like "12/31/2018" is
 * fully excluded — none of "12", "31", "2018" survive as citable.
 */
export function findCitableNumerics(
  text: string,
  opts: {
    /**
     * `text` is the text node directly after a backend citation link. A dash
     * that opens it is the separator of a range whose first number is that
     * link, never a minus sign (#12190).
     */
    followsCitationLink?: boolean;
  } = {}
): CitableNumericMatch[] {
  if (!text || /CHART_DATA_.*_PRESERVED/.test(text)) {
    return [];
  }

  const excludedDigits = new Set<string>();
  const collect = (pattern: RegExp, wholeMatch = false): void => {
    const re = new RegExp(pattern.source, "gi");
    for (const m of text.matchAll(re)) {
      if (wholeMatch) {
        excludedDigits.add(m[0]);
        continue;
      }
      for (const d of m[0].match(/\d+/g) || []) {
        excludedDigits.add(d);
      }
    }
  };
  collect(EXCLUDE_RE);
  collect(DATE_RE);
  collect(ISO_DATE_RE);
  collect(DURATION_RE);
  collect(SLASH_RE);
  collect(STOCK_INDEX_RE);
  collect(INDUSTRY_TERMS_RE);
  collect(SECTION_REFERENCE_RE);
  collect(YEAR_RE, true);

  const out: CitableNumericMatch[] = [];
  // Spans a range or a range orphan has claimed: the single-number scan below
  // gives way to them, so "$540–900B" is never also "$540".
  const claimed: Array<[number, number]> = [];

  // The second half of a range the backend linked the first half of. The
  // dash is claimed with it so it is never read as a sign, whether or not the
  // number after it turns out to be a figure.
  const orphan = opts.followsCitationLink ? RANGE_ORPHAN_RE.exec(text) : null;
  if (orphan) {
    const value = orphan[1];
    const end = orphan[0].length;
    claimed.push([0, end]);
    if (isFigureAlone(value) && !excludedDigits.has(bareDigits(value))) {
      out.push({ start: end - value.length, end, value, rangeOrphan: true });
    }
  }

  // A dash range is one figure, mirroring the backend's _answer_ranges
  // (#12190). The unit is routinely written once: a leading currency symbol
  // is shared from the first number to the second ("$540–900B"), a trailing
  // suffix or percent from the second to the first ("69–70%").
  const protectedSpans: Array<[number, number]> = [
    ...text.matchAll(new RegExp(RANGE_PROTECTED_RE_SRC, "gi")),
  ].map((u) => [u.index ?? 0, (u.index ?? 0) + u[0].length]);
  for (const r of text.matchAll(new RegExp(RANGE_RE_SRC, "gi"))) {
    const start = r.index ?? 0;
    const end = start + r[0].length;
    if (
      [...claimed, ...protectedSpans].some(([s, e]) => start < e && s < end)
    ) {
      continue;
    }
    const [value, left, right] = [r[0], r[1], r[2]];
    const lead = RANGE_LEAD_RE.exec(left);
    const rightLead = RANGE_LEAD_RE.exec(right);
    const leftTrail = RANGE_TRAIL_RE.exec(left);
    const trail = RANGE_TRAIL_RE.exec(right);
    // A trailing unit on the first number only ("2%-20"), or a currency on
    // the second only, is two quantities rather than a range.
    if ((leftTrail && !trail) || (rightLead && !lead)) {
      continue;
    }
    if (!DECORATED_RE.test(value)) {
      continue;
    }
    // A year, a date part or a duration is never the end of a range of
    // figures ("2033–2034", "$100–2023 vintage", "$5-10 year plan").
    if ([left, right].some((e) => excludedDigits.has(bareDigits(e)))) {
      continue;
    }
    // A number leading a TitleCase word is a name, unless the range carries a
    // decoration no name leads with. Corresponds to Python:
    // _NAME_GUARD_EXEMPT_RE.
    if (
      !RANGE_NAME_GUARD_EXEMPT_RE.test(value) &&
      /^\s+[A-Z][a-z]/.test(text.slice(end))
    ) {
      continue;
    }
    const candidatesFor = (written: string, shared: string): string[] => [
      ...(isFigureAlone(written) ? [written] : []),
      ...(shared !== written ? [shared] : []),
    ];
    claimed.push([start, end]);
    out.push({
      start,
      end,
      value,
      range: {
        left: {
          start,
          end: start + left.length,
          value: left,
          candidates: candidatesFor(
            left,
            trail && !leftTrail ? `${left}${trail[0]}` : left
          ),
        },
        right: {
          start: end - right.length,
          end,
          value: right,
          candidates: candidatesFor(
            right,
            lead && !rightLead ? `${lead[0]}${right}` : right
          ),
        },
      },
    });
  }

  const numRe = new RegExp(NUM_RE.source, "gi");
  let m: RegExpExecArray | null;
  while ((m = numRe.exec(text)) !== null) {
    if (m[0].length === 0) {
      numRe.lastIndex += 1;
      continue;
    }
    const matchStart = m.index;
    const matchEnd = m.index + m[0].length;
    if (claimed.some(([s, e]) => matchStart < e && s < matchEnd)) {
      continue;
    }
    // The comparison-prefix group can swallow leading whitespace; trim it so
    // the highlighted span starts at the figure itself.
    const lead = m[0].length - m[0].trimStart().length;
    const value = m[0].trimStart();
    const bare = value.replace(/[^\d.]/g, "").replace(/^\.+|\.+$/g, "");
    if (!bare || excludedDigits.has(bare)) {
      continue;
    }
    // Undecorated 1-3 digit integers are not linkable figures (#10527).
    if (!DECORATED_RE.test(value) && value.replace(/\D/g, "").length < 4) {
      continue;
    }
    // A number leading a TitleCase word is a name ("12 West", "3 Sigma"),
    // not a figure (#10527).
    if (/^\s+[A-Z][a-z]/.test(text.slice(m.index + m[0].length))) {
      continue;
    }
    let start = m.index + lead;
    let end = m.index + m[0].length;
    let spanValue = value;
    // Accounting-parenthesis extension (#10654): a negative figure written as
    // "$(1,234)", "($1,234)", or "(1,234)" should link the wrapping parens —
    // and any currency symbol just outside them — as one span, mirroring the
    // legacy backend injector (inject_citations.py). NUM_RE stops at the digits
    // because the "(" breaks the currency symbol off, so widen the span here.
    if (text[start - 1] === "(" && text[end] === ")") {
      start -= 1;
      end += 1;
      if (start > 0 && CURRENCY_SYMBOLS.includes(text[start - 1])) {
        start -= 1;
      }
      // The unit can sit OUTSIDE the parens too ("(11.78)%"): the trailing
      // percent joins the span for the same reason the currency does,
      // mirroring the backend injector (#11454 live testing).
      if (text[end] === "%") {
        end += 1;
      }
      spanValue = text.slice(start, end);
    }
    out.push({
      start,
      end,
      value: spanValue,
    });
  }
  return out.sort((a, b) => a.start - b.start);
}

const escapeForRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const withThousandsSeparators = (digits: string): string => {
  const groups: string[] = [];
  let rest = digits;
  while (rest.length > 3) {
    groups.unshift(rest.slice(-3));
    rest = rest.slice(0, -3);
  }
  groups.unshift(rest);
  return groups.join(",");
};

// Scaled magnitude suffixes, mirroring the backend injector's suffix_map:
// "$238.0 M" may bind against a source writing "238,000,000" (within 5%).
// Other unit suffixes (x, bp/bps, pp/ppt, t) have no scaled form and fall
// through to literal digit matching, exactly like the backend.
const SCALED_SUFFIXES: Array<[string, number]> = [
  ["k", 1e3],
  ["mm", 1e6],
  ["million", 1e6],
  ["m", 1e6],
  ["bn", 1e9],
  ["billion", 1e9],
  ["b", 1e9],
  ["trillion", 1e12],
];

/**
 * Whether a citable figure is actually present in a source chunk's text, so
 * the renderer may bind it to that source as an inline clickable citation.
 *
 * Mirrors the backend injector's post-#10706 validation contract
 * (inject_citations.py): a percent figure only matches a percent-marked source
 * value (never a bare digit coincidence like a rating count or a "2Q22"
 * label), a number never matches a different decimal of the same integer part
 * ("22" must not bind via "22.8"), all-zero fractions stay equivalent
 * ("22" ~ "22.0", "8.6" ~ "8.60"), OCR digit-spacing is tolerated
 * ("0. 31%", "4 100"), and magnitude figures also match their scaled form.
 * ``spreadsheet`` sources skip the percent requirement (the mark lives in a
 * column header, not the cell) and the OCR variants (Excel numbers are clean),
 * mirroring the backend's is_excel handling.
 */
export function numericBindsToSource(
  token: string,
  content: string | null | undefined,
  opts: { spreadsheet?: boolean } = {}
): boolean {
  if (!content) {
    return false;
  }
  const nums = token.match(/\d+(?:,\d{3})*(?:\.\d+)?/g);
  if (!nums || nums.length === 0) {
    return false;
  }
  const spreadsheet = opts.spreadsheet === true;

  const tokenLower = token.toLowerCase();
  const scaleIn = (segment: string): number | null => {
    for (const [suf, factor] of SCALED_SUFFIXES) {
      // Anchored to a preceding digit so "$1.2M" (no space) counts too; a bare
      // \b can't split "2m" because digits and letters are both word chars.
      if (new RegExp(`(?<=[\\d.])\\s*${suf}\\b`).test(segment)) {
        return factor;
      }
    }
    return null;
  };
  // Per number, not once for the token (#12190). A range token carries two
  // numbers: matching the first alone let "$1.2M–$1.5M" bind whole against a
  // source holding only 1,200,000, and one suffix for the pair read the 1.2B
  // of "$1.2B–$900M" as 1.2 million. Each number takes the suffix written
  // after it; a number with none shares the last one ("$20–25 million").
  const numberSpans = [...tokenLower.matchAll(/\d+(?:,\d{3})*(?:\.\d+)?/g)];
  const ownScales = numberSpans.map((span, i) =>
    scaleIn(tokenLower.slice(span.index ?? 0, numberSpans[i + 1]?.index))
  );
  const sharedScale =
    [...ownScales].reverse().find((own) => own !== null) ?? null;
  const bindsScaled = (num: string, i: number): boolean => {
    const base = parseFloat(num.replace(/,/g, ""));
    const scale = ownScales[i] ?? sharedScale;
    if (scale === null || Number.isNaN(base)) {
      return false;
    }
    const target = base * scale;
    const tolerance = 0.05 * target;
    return (content.match(/\d+(?:,\d{3})*(?:\.\d+)?/g) ?? []).some(
      (candidate) => {
        const value = parseFloat(candidate.replace(/,/g, ""));
        return !Number.isNaN(value) && Math.abs(value - target) <= tolerance;
      }
    );
  };

  const requirePct = token.includes("%") && !spreadsheet;
  return nums.every((num, i) => {
    if (bindsScaled(num, i)) {
      return true;
    }
    const plain = num.replace(/[,\s]/g, "");
    const dot = plain.indexOf(".");
    const intPart = dot === -1 ? plain : plain.slice(0, dot);
    const frac = dot === -1 ? "" : plain.slice(dot + 1);
    // Extra trailing zeros in the source denote the same figure ("8.60" for
    // 8.6, "22.0" for 22); a different fraction is a different figure.
    const zeroFrac = /^0*$/.test(frac);
    const corePlain = zeroFrac ? intPart : plain;
    const grouped = withThousandsSeparators(intPart);
    const variants = [escapeForRegExp(corePlain)];
    if (grouped !== intPart) {
      variants.push(escapeForRegExp(zeroFrac ? grouped : `${grouped}.${frac}`));
    }
    if (!spreadsheet) {
      // OCR digit-spacing tolerance, mirroring the backend's sloppy/fuzzy
      // variants ("0. 31", "4 100"). Skipped for spreadsheets.
      variants.push(plain.split("").map(escapeForRegExp).join("\\s*"));
      if (corePlain !== plain) {
        variants.push(corePlain.split("").map(escapeForRegExp).join("\\s*"));
      }
    }
    const tail = zeroFrac ? "(?:\\.0+)?" : "0*";
    const body = `(?<![\\d.])(?:${variants.join("|")})${tail}(?!\\d)(?!\\.\\d)`;
    if (!requirePct) {
      return new RegExp(body).test(content);
    }
    // The first number of a source-side percent range shares the mark
    // written after the second ("69" in "69–70%"), as the backend's range
    // pattern accepts (#12190).
    const rangeTail = `(?:\\s?[${DASH_CHARS}]\\s?\\d+(?:,\\d{3})*(?:\\.\\d+)?)?`;
    if (
      new RegExp(`${body}${rangeTail}\\s*(?:%|percent\\b|pct\\b)`).test(content)
    ) {
      return true;
    }
    // PDF table chunks routinely carry the % in a column header with bare
    // cells ("| 21.9 |" under "Margin (%)"), so an unambiguous numeric — a
    // nonzero decimal or 4+ digits, the #10527/#10561 ambiguity bar — may
    // match bare, as long as the occurrence isn't a magnitude quantity
    // ("USD 16 billion" must never validate 16.0%). Short bare integers
    // (the "5" of "30|5|1", the "22" of "2Q22") stay percent-only.
    const unambiguous = !zeroFrac || intPart.length >= 4;
    if (!unambiguous) {
      return false;
    }
    return new RegExp(
      `${body}(?!\\s*(?:trillion|billion|million|bn|mm|m|k|b)\\b)`,
      "i"
    ).test(content);
  });
}
